import styles from './_dealer-editor-dialog.module.scss';
import { Window } from '@progress/kendo-react-dialogs';
import { WindowActionsBar } from '@progress/kendo-react-dialogs';
import { useState } from 'react';
import { Checkbox, Input, InputChangeEvent, InputPrefix, InputSeparator, InputSuffix, NumericTextBox, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { ComboBox, ComboBoxChangeEvent } from '@progress/kendo-react-dropdowns';
import { Divider, IconButton } from '@mui/material';
import { Typography } from '@progress/kendo-react-common';
import { AccountCreationInfo, ClientMetaData, Dealer, UpdateDealer } from '../../../../Models/Dealer';
import { Address } from '../../../../Models/Address';
import { ILookupItem } from '../../../../Models/LookupItem';
import { StateAbbreviations } from '../../../../Models/StateAbbreviations';
import { includes } from 'ramda';
import { hasValue, FormInput, FormComboBox } from '@amsanalytics/react-shared-library';
import { Field, Form, FormElement, FormRenderProps } from '@progress/kendo-react-form';

export interface DealerEditorDialogProps {
  dealer: Dealer | null;
  timeZones: string[];
  usedClientIds: string[];
  onClose: () => void;
  onSubmit: (dealer: UpdateDealer) => void;
}


export interface DealerEditInfo {
  id?: string;
  name: string | null;
  timeZone: string | null;
  clientAddress: Partial<Address>;
  clientMetaData: Partial<ClientMetaData> | null;
  accountCreationInfo: AccountCreationInfo | null;
}
const requiredFieldValidator = (value: any, valueGetter: (name: string) => any, fieldProps: { name: string }, required?: boolean) => {
  // You can use `additionalInfo` inside the validator
  return value?.length > 0 ? undefined : `Field is required`;
};

export default function DealerEditorDialog(props: DealerEditorDialogProps) {
  const { dealer, timeZones } = { ...props };
  const [dealerEditInfo, setDealerEditInfo] = useState<Partial<DealerEditInfo>>(dealer ? {
    ...dealer,
    name: dealer.name,
    timeZone: dealer.timeZone,
    clientAddress: dealer.clientAddress ? {
      ...dealer.clientAddress
    } : {},
    clientMetaData: dealer.clientMetaData ? {
      ...dealer.clientMetaData
    } : {},
    accountCreationInfo: null

  } : {
    clientMetaData: {},
    clientAddress: {},
    accountCreationInfo: {}
  });
  const isValid = () => {
    return hasValue(dealerEditInfo.name)
      && hasValue(dealerEditInfo.timeZone)
      && hasValue(dealerEditInfo.id)
      && dealerEditInfo.id!.length >= 3;
      // && (!dealerEditInfo.id! || !includes(dealerEditInfo.id, props.usedClientIds)
  }
  const isNewDealer = dealer === null;
  const initialValues = { dealerName: dealerEditInfo.name, dealerCode: dealerEditInfo.id, dealerTimeZone: dealerEditInfo.timeZone}
  return (
    <div className={styles.dealerEditorDialogContainer}>
      <Window
        initialWidth={700}
        initialHeight={props.dealer?.id ? 800 : 900}
        title={dealer === null ? 'Add Dealer' : `Edit Dealer - ${dealer.name}`}
        modal={true}
        onClose={props.onClose}
      >
        <div
          style={{
            display: 'flex',
            flex: 1,
            flexDirection: 'column',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            padding: '10px',
            marginRight: '5px',
            height: '100%',
            overflowY: 'auto'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <div style={{ display: 'flex', flexDirection: 'row', width: '100%' }}>
              <Form
                initialValues={initialValues}
                render={(formRenderProps: FormRenderProps) => (
                  <FormElement style={{ width: '100%' }}>
                    <fieldset className={"k-form-fieldset"}>
                      <legend className={"k-form-legend"} style={{marginBottom: '0px'}} >
                        <span style={{ fontWeight: '500'}}>{'Dealer'}</span>
                      </legend>
                      <div style={{display: 'flex', alignItems: 'flex-start'}}>
                        <Field
                          style={{ width: '100%' }}
                          fieldWrapperStyle={{  width: '60%'}}
                          formFieldWrapperStyle={{ width: '100%'}}
                          name="dealerName"
                          id="dealerName"
                          type="text"
                          // useFloatingLabel={true}
                          label="Dealer Name *"
                          value={dealerEditInfo.name! ?? null}
                          onChange={(event: InputChangeEvent) => {
                            setDealerEditInfo({
                              ...dealerEditInfo,
                              name: event.value
                            });
                          }}
                          component={FormInput}
                          required={true}
                          validator={(value: any, valueGetter: (name: string) => any, fieldProps: { name: string }, required?: boolean) => {
                            return value?.length > 0 ? undefined : `Dealer Name is required`;;
                          }}
                        />
                        <Field
                          name="dealerCode"
                          id="dealerCode"
                          label="Dealer Code *"
                          value={dealerEditInfo.id! ?? null}
                          disabled={!isNewDealer}
                          style={{width: '100px' }}
                          fieldWrapperStyle={{ marginLeft: '10px'}}
                          // formFieldWrapperStyle={{ width: '110px'}}
                          required={true}
                          component={FormInput}
                          // useFloatingLabel={true}
                          pattern="[a-zA-Z0-9]"
                          formatValue={(value: any) => {
                            return value?.replace(/[^a-zA-Z0-9]/g, '')?.toUpperCase()
                          }}
                          onChange={(event: InputChangeEvent) => {
                            setDealerEditInfo({
                              ...dealerEditInfo,
                              id: event.value?.replace(/[^a-zA-Z0-9]/g, '')?.toUpperCase()
                            });
                          }}
                          validator={(value: any, valueGetter: (name: string) => any, fieldProps: { name: string }, required?: boolean) => {
                            return !hasValue(value) ? 'Dealer code is required' : value!.length <= 2 ? 'Dealer code must be at least 3 characters' : includes(value, props.usedClientIds) ? 'Dealer code is already in use' : '';
                          }}                        
                        />
                      </div>
                      <Field
                        style={{ width: '220px' }}
                        fieldWrapperStyle={{  width: '60%', marginTop: '12px'}}
                        formFieldWrapperStyle={{ width: '100%'}}
                        label="Dealer Time Zone*"
                        name="dealerTimeZone"
                        id="dealerTimeZone"
                        // useFloatingLabel={true}                        
                        value={timeZones.find(
                          (tz) => tz === dealerEditInfo.timeZone
                        )}
                        onChange={(event: ComboBoxChangeEvent) => {
                          setDealerEditInfo({
                            ...dealerEditInfo,
                            timeZone: event.value
                          });
                        }}
                        component={FormComboBox}
                        data={timeZones}                        
                        required={true}
                        clearButton={true}
                        validator={(value: any, valueGetter: (name: string) => any, fieldProps: { name: string }, required?: boolean) => {
                          return value ? undefined : `Time zone is required`;
                        }}
                      />
                    </fieldset>
                    <fieldset className={"k-form-fieldset"}>
                      <legend className={"k-form-legend"} >
                        <span style={{ fontWeight: '500'}}>{'Dealer Address'}</span>
                      </legend>
                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between'
                          }}
                        >

                          <Input
                            label="Address"
                            value={dealerEditInfo.clientAddress!.addressLine1! ?? null}
                            style={{ marginRight: '10px', width: '100%' }}
                            onChange={(event: InputChangeEvent) => {
                              setDealerEditInfo({
                                ...dealerEditInfo,
                                clientAddress: {
                                  ...dealerEditInfo.clientAddress!,
                                  addressLine1: event.value
                                }
                              });
                            }}
                          />                          
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between'
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'flex-start'
                            }}
                          >
                            <Input
                              label="City"
                              value={dealerEditInfo.clientAddress!.city! ?? null}
                              style={{ width: '180px', marginRight: '10px' }}
                              onChange={(event: InputChangeEvent) => {
                                setDealerEditInfo({
                                  ...dealerEditInfo,
                                  clientAddress: {
                                    ...dealerEditInfo.clientAddress!,
                                    city: event.value
                                  }
                                });
                              }}
                            />
                            <ComboBox
                              value={StateAbbreviations.find(
                                (s) => s === dealerEditInfo.clientAddress!.stateAbbreviation
                              )}
                              data={StateAbbreviations}
                              label="State"
                              style={{ marginRight: '20px', width: '100px' }}
                              onChange={(event: ComboBoxChangeEvent) => {
                                setDealerEditInfo({
                                  ...dealerEditInfo,
                                  clientAddress: {
                                    ...dealerEditInfo.clientAddress!,
                                    stateAbbreviation: event.value
                                  }
                                });
                              }}
                            />
                            <Input
                              label="Zip"
                              value={dealerEditInfo.clientAddress!.zipCode! ?? null}
                              style={{ marginLeft: '10px', marginRight: '10px', width: '20%' }}
                              onChange={(event: InputChangeEvent) => {
                                setDealerEditInfo({
                                  ...dealerEditInfo,
                                  clientAddress: {
                                    ...dealerEditInfo.clientAddress!,
                                    zipCode: event.value
                                  }
                                });
                              }}
                            />
                          </div>
                        </div>
                      </div>

                    </fieldset>
                    <fieldset className={"k-form-fieldset"}>
                      <legend className={"k-form-legend"} >
                        <span style={{ fontWeight: '500'}}>{'Dealer Details'}</span>
                      </legend>
                      <div className={styles.additionalInformation}>
                        <div className={styles.businessLines}>
                          <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                            <div className={styles.businessLineItem}>
                              <Typography.h6 style={{ padding: '0px', margin: '0px', display: 'flex', flex: 1, width: '50%' }}>Business Lines<span style={{ fontWeight: 'normal', marginLeft: '5px' }}>(select all that apply):</span></Typography.h6>
                              <Typography.h6 style={{ padding: '0px', margin: '0px', display: 'flex', flex: 1, width: '50%' }}>Lots:</Typography.h6>
                            </div>
                            <div className={styles.businessLineItem}>
                              <div style={{ width: '50%' }}>
                                <Checkbox
                                  label={'Buy Here Pay Here'}
                                  checked={dealerEditInfo.clientMetaData?.isBuyHerePayHere ?? false}
                                  onChange={(event) => {
                                    setDealerEditInfo({
                                      ...dealerEditInfo,
                                      clientMetaData: {
                                        ...dealerEditInfo.clientMetaData!,
                                        isBuyHerePayHere: event.value,
                                        numberOfBuyHerePayHereLots: event.value ? (dealerEditInfo.clientMetaData?.numberOfBuyHerePayHereLots ?? 1) : null
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <NumericTextBox
                                min={1}
                                style={{ width: '100px' }}
                                value={dealerEditInfo.clientMetaData?.numberOfBuyHerePayHereLots ?? null}
                                onChange={(event) => {
                                  setDealerEditInfo({
                                    ...dealerEditInfo,
                                    clientMetaData: {
                                      ...dealerEditInfo.clientMetaData!,
                                      numberOfBuyHerePayHereLots: event.value
                                    }
                                  });
                                }}
                              />
                            </div>
                            <div className={styles.businessLineItem}>
                              <div style={{ width: '50%' }}>
                                <Checkbox
                                  label={'Retail'}
                                  checked={dealerEditInfo.clientMetaData?.isRetail ?? false}
                                  onChange={(event) => {
                                    setDealerEditInfo({
                                      ...dealerEditInfo,
                                      clientMetaData: {
                                        ...dealerEditInfo.clientMetaData!,
                                        isRetail: event.value,
                                        numberOfRetailLots: event.value ? (dealerEditInfo.clientMetaData?.numberOfRetailLots ?? 1) : null
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <NumericTextBox
                                min={1}
                                style={{ width: '100px' }}
                                value={dealerEditInfo.clientMetaData?.numberOfRetailLots ?? null}
                                onChange={(event) => {
                                  setDealerEditInfo({
                                    ...dealerEditInfo,
                                    clientMetaData: {
                                      ...dealerEditInfo.clientMetaData!,
                                      numberOfRetailLots: event.value
                                    }
                                  });
                                }}
                              />
                            </div>
                            <div className={styles.businessLineItem}>
                              <div style={{ width: '50%' }}>
                                <Checkbox
                                  label={'Lease Here Pay Here'}
                                  checked={dealerEditInfo.clientMetaData?.isLeaseHerePayHere ?? false}
                                  onChange={(event) => {
                                    setDealerEditInfo({
                                      ...dealerEditInfo,
                                      clientMetaData: {
                                        ...dealerEditInfo.clientMetaData!,
                                        isLeaseHerePayHere: event.value,
                                        numberOfLeaseHerePayHereLots: event.value ? (dealerEditInfo.clientMetaData?.numberOfLeaseHerePayHereLots ?? 1) : null
                                      }
                                    });
                                  }}
                                />
                              </div>
                              <NumericTextBox
                                min={1}
                                style={{ width: '100px' }}
                                value={dealerEditInfo.clientMetaData?.numberOfLeaseHerePayHereLots ?? null}
                                onChange={(event) => {
                                  setDealerEditInfo({
                                    ...dealerEditInfo,
                                    clientMetaData: {
                                      ...dealerEditInfo.clientMetaData!,
                                      numberOfLeaseHerePayHereLots: event.value
                                    }
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </div>
                        <div className={styles.dataAutomation}>
                          <Typography.h6 style={{ padding: '0px', margin: '0px' }}>Software for possible NIADA data automation:</Typography.h6>
                          {/* Put the  Metadata here for dealerEditInfo.clientMetaData. Make them all string inputs and implement the onChange */}
                          <div className={styles.dataAutomationItems}>
                            <div className={styles.dataAutomationItemColumn}>
                              <Input
                                label="DMS"
                                style={{ width: '100%' }}
                                value={dealerEditInfo.clientMetaData?.dealerManagementSoftware! ?? null}
                                onChange={(event: InputChangeEvent) => {
                                  setDealerEditInfo({
                                    ...dealerEditInfo,
                                    clientMetaData: {
                                      ...dealerEditInfo.clientMetaData!,
                                      dealerManagementSoftware: event.value
                                    }
                                  });
                                }}
                              />
                              <Input
                                label="CRM"
                                style={{ width: '100%' }}
                                value={dealerEditInfo.clientMetaData?.customerRelationsManagementSoftware! ?? null}
                                onChange={(event: InputChangeEvent) => {
                                  setDealerEditInfo({
                                    ...dealerEditInfo,
                                    clientMetaData: {
                                      ...dealerEditInfo.clientMetaData!,
                                      customerRelationsManagementSoftware: event.value
                                    }
                                  });
                                }}
                              />
                            </div>
                            <div className={styles.dataAutomationItemColumn}>
                              <Input
                                label="Accounting"
                                style={{ width: '100%' }}
                                value={dealerEditInfo.clientMetaData?.accountingSoftware! ?? null}
                                onChange={(event: InputChangeEvent) => {
                                  setDealerEditInfo({
                                    ...dealerEditInfo,
                                    clientMetaData: {
                                      ...dealerEditInfo.clientMetaData!,
                                      accountingSoftware: event.value
                                    }
                                  });
                                }}
                              />
                              <Input
                                label="Website URL"
                                style={{ width: '100%' }}
                                value={dealerEditInfo.clientMetaData?.websiteUrl! ?? null}
                                onChange={(event: InputChangeEvent) => {
                                  setDealerEditInfo({
                                    ...dealerEditInfo,
                                    clientMetaData: {
                                      ...dealerEditInfo.clientMetaData!,
                                      websiteUrl: event.value
                                    }
                                  });
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>                      
                    </fieldset>

                      {isNewDealer && (
                        <fieldset className={"k-form-fieldset"}>
                          <legend className={"k-form-legend"} >
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                              <Typography.h6 style={{ padding: '0px', margin: '0px' }}>Invite First User</Typography.h6><span>&nbsp;(Optional. This can be done later.)</span>
                            </div>
                          </legend>
                          <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between'
                          }}>                            
                            <TextBox
                              title="Username"
                              placeholder="Enter username"
                              onChange={(event: TextBoxChangeEvent) => {
                                setDealerEditInfo({
                                  ...dealerEditInfo,
                                  accountCreationInfo: {
                                    ...dealerEditInfo.accountCreationInfo!,
                                    username: event.value as string
                                  }
                                });
                              }}
                              suffix={() => (
                                <div style={{ background: 'lightgrey', display: 'flex', alignItems: 'center' }}>
                                  {/* <InputSeparator orientation="vertical" /> */}

                                  <InputSuffix orientation="horizontal">
                                    <span className="k-input-suffix-text">@amsanalytics.com</span>
                                  </InputSuffix>
                                </div>
                              )}

                            />
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: '10px'
                          }}>
                            <Input
                              label="First Name"
                              value={dealerEditInfo.accountCreationInfo!.firstName! ?? null}
                              onChange={(event: InputChangeEvent) => {
                                setDealerEditInfo({
                                  ...dealerEditInfo,
                                  accountCreationInfo: {
                                    ...dealerEditInfo.accountCreationInfo!,
                                    firstName: event.value
                                  }
                                });
                              }}
                              style={{ marginRight: '10px', width: '50%' }}
                            />
                            <Input
                              label="Last Name"
                              value={dealerEditInfo.accountCreationInfo!.lastName! ?? null}
                              onChange={(event: InputChangeEvent) => {
                                setDealerEditInfo({
                                  ...dealerEditInfo,
                                  accountCreationInfo: {
                                    ...dealerEditInfo.accountCreationInfo!,
                                    lastName: event.value
                                  }
                                });
                              }}
                              style={{ marginRight: '10px', width: '50%' }}
                            />
                          </div>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            marginTop: '10px'
                          }}>
                            <Input
                              label="Email Address"
                              value={dealerEditInfo.accountCreationInfo!.emailAddress! ?? null}
                              onChange={(event: InputChangeEvent) => {
                                setDealerEditInfo({
                                  ...dealerEditInfo,
                                  accountCreationInfo: {
                                    ...dealerEditInfo.accountCreationInfo!,
                                    emailAddress: event.value
                                  }
                                });
                              }}
                              style={{ marginRight: '10px', width: '50%' }}
                            />
                            <Input
                              label="Phone Number"
                              value={dealerEditInfo.accountCreationInfo!.phoneNumber! ?? null}
                              onChange={(event: InputChangeEvent) => {
                                setDealerEditInfo({
                                  ...dealerEditInfo,
                                  accountCreationInfo: {
                                    ...dealerEditInfo.accountCreationInfo!,
                                    phoneNumber: event.value
                                  }
                                });
                              }}
                              style={{ marginRight: '10px', width: '50%' }}
                            />
                          </div>
                        </fieldset>
                      )}                      
                    
                  </FormElement>
                )}
              />
            </div>
          </div>

        </div>
        <WindowActionsBar layout={'stretched'}>
          <button
            type="button"
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-base"
            onClick={() => { props.onClose() }}
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!isValid()}
            className="k-button k-button-md k-rounded-md k-button-solid k-button-solid-primary"
            onClick={() => {
              props.onSubmit({
                ...(dealerEditInfo as UpdateDealer),
                clientAddress: dealerEditInfo.clientAddress ? {
                  ...dealerEditInfo.clientAddress
                } : null,
                clientMetaData: dealerEditInfo.clientMetaData ? {
                  ...(dealerEditInfo.clientMetaData as ClientMetaData)
                } : null,
              });
            }}
          >
            {isNewDealer ? 'Create Dealer' : 'Update Dealer'}
          </button>
        </WindowActionsBar>
      </Window>
    </div>
  );
}
